import { type ChangeEvent,useCallback, useRef, useState } from 'react'

import { listReferenceImages, type ReferenceImageItem } from '../api/referenceImages'
import { absolutePublicUploadUrl, uploadReferenceImageToDisk } from '../api/referenceUpload'
import { useToast } from '../context/ToastContext'
import { showApiErrorToast } from '../lib/apiErrorToast'
import { isAbortError } from '../lib/isAbortError'
import {
  LIBRARY_REFERENCE_UPLOAD_SEGMENT,
  REFERENCE_IMAGE_MAX_BYTES,
  REFERENCE_IMAGE_MAX_SIZE_LABEL,
  referenceImageUploadFolderPath,
} from '../lib/libraryPaths'
import { createLogger } from '../lib/logger'
import { mediaFrameImageClass } from '../lib/mediaFrameFit'
import { ChromeToolbarButton } from './ChromeActions'
import { ChromeMediaUploadSlot } from './ChromeMediaUploadSlot'
import { chromeFieldBaseClass } from './ChromeSurface'
import { IconLibraryAllFolders } from './nav-icons'
import {
  LibraryReferencePickerModal,
  type LibraryReferencePickerTab,
  referencePickerSubmitUrl,
} from './reference/LibraryReferencePickerModal'
import { ThemedImage } from './ThemedImage'

const pickerLogger = createLogger('chrome-reference-image-picker')

/** 选择器单页条数：与后端 size 上限（100）对齐，按页增量渲染避免一次铺满 DOM */
const PICKER_PAGE_SIZE = 60

function previewRound(angular: boolean): string {
  return angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]'
}

export type ChromeReferenceImagePickerProps = {
  angular: boolean
  mode?: 'single' | 'multi'
  urls: string[]
  onChange: (urls: string[]) => void
  maxCount?: number
  hint?: string
  compact?: boolean
  disabled?: boolean
  emptyPlaceholder?: string
  pickerTitle?: string
  /** 已达 maxCount 时点击添加的提示（不传则用默认文案） */
  atLimitMessage?: string
}

export function ChromeReferenceImagePicker({
  angular,
  mode = 'multi',
  urls,
  onChange,
  maxCount: maxCountProp,
  hint,
  compact = false,
  disabled = false,
  emptyPlaceholder,
  pickerTitle,
  atLimitMessage,
}: ChromeReferenceImagePickerProps) {
  const maxCount = maxCountProp ?? (mode === 'single' ? 1 : 6)
  const { showToast } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [activeTab, setActiveTab] = useState<LibraryReferencePickerTab>('gallery')
  const [libraryItems, setLibraryItems] = useState<ReferenceImageItem[]>([])
  const [recentItems, setRecentItems] = useState<ReferenceImageItem[]>([])
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [recentLoading, setRecentLoading] = useState(false)
  const [catalogTotal, setCatalogTotal] = useState(0)
  const [recentTotal, setRecentTotal] = useState(0)
  const catalogPageRef = useRef(0)
  const recentPageRef = useRef(0)
  const loadingMoreRef = useRef(false)

  const remaining = Math.max(0, maxCount - urls.length)
  const btnClass = compact ? 'h-8 w-full justify-center px-3 text-xs' : 'h-9 w-full justify-center'
  const placeholderText = emptyPlaceholder ?? (mode === 'single' ? '点击从图库选择' : '从图库选择参考图')
  const modalTitle = pickerTitle ?? (mode === 'single' ? '选择造型图' : '选择参考图')

  const mergeUrls = useCallback(
    (incoming: string[]) => {
      if (mode === 'single') {
        const first = incoming.find((u) => u.trim())?.trim()
        onChange(first ? [first] : [])
        return
      }
      const merged = [...urls]
      for (const u of incoming) {
        const t = u.trim()
        if (!t || merged.includes(t)) continue
        merged.push(t)
        if (merged.length >= maxCount) break
      }
      onChange(merged)
    },
    [mode, maxCount, onChange, urls],
  )

  const notifyAtLimit = useCallback(() => {
    showToast({
      message:
        atLimitMessage ??
        (mode === 'single'
          ? '参考图已达上限'
          : `参考图已达上限（最多 ${maxCount} 张）`),
      variant: 'warning',
    })
  }, [atLimitMessage, maxCount, mode, showToast])

  /**
   * 按页拉取选择器候选：「上传记录」（本地磁盘子树，默认选中，后端按时间倒序）与图库（云）分源。
   * 上传的参考图落库在独立子树，不作为图库默认入口，故单独成区。
   */
  const loadTabPage = useCallback(
    async (tab: LibraryReferencePickerTab, reset: boolean) => {
      const isRecent = tab === 'recent'
      const pageRef = isRecent ? recentPageRef : catalogPageRef
      const setItems = isRecent ? setRecentItems : setLibraryItems
      const setTotal = isRecent ? setRecentTotal : setCatalogTotal
      const setLoading = isRecent ? setRecentLoading : setCatalogLoading
      const nextPage = reset ? 1 : pageRef.current + 1
      if (reset) {
        pageRef.current = 0
        setItems([])
        setTotal(0)
      }
      setLoading(true)
      try {
        const res = await listReferenceImages(
          nextPage,
          PICKER_PAGE_SIZE,
          undefined,
          isRecent ? LIBRARY_REFERENCE_UPLOAD_SEGMENT : undefined,
        )
        const usable = res.items.filter((item) => item.url.trim())
        pageRef.current = nextPage
        setTotal(res.total)
        setItems((prev) => {
          if (reset) return usable
          const seen = new Set(prev.map((item) => item.publicId))
          return [...prev, ...usable.filter((item) => !seen.has(item.publicId))]
        })
      } catch (e) {
        if (!isAbortError(e)) {
          showApiErrorToast(showToast, e, '列表加载失败', 'error')
        }
        if (reset) setItems([])
      } finally {
        setLoading(false)
        loadingMoreRef.current = false
      }
    },
    [showToast],
  )

  const openPicker = useCallback(() => {
    if (disabled) return
    if (remaining <= 0) {
      notifyAtLimit()
      return
    }
    setSelectedIds([])
    // 默认选中「上传记录」；图库首次切到该分区时再拉取
    setActiveTab('recent')
    recentPageRef.current = 0
    setRecentItems([])
    setRecentTotal(0)
    setPickerOpen(true)
    void loadTabPage('recent', true)
  }, [disabled, notifyAtLimit, remaining, loadTabPage])

  const handleTabChange = useCallback(
    (tab: LibraryReferencePickerTab) => {
      setActiveTab(tab)
      if ((tab === 'recent' ? recentPageRef.current : catalogPageRef.current) === 0) {
        void loadTabPage(tab, true)
      }
    },
    [loadTabPage],
  )

  const handleLoadMore = useCallback(() => {
    if (loadingMoreRef.current) return
    const loaded = activeTab === 'recent' ? recentItems : libraryItems
    const total = activeTab === 'recent' ? recentTotal : catalogTotal
    if (loaded.length >= total) return
    loadingMoreRef.current = true
    void loadTabPage(activeTab, false)
  }, [activeTab, catalogTotal, libraryItems, loadTabPage, recentItems, recentTotal])

  const toggleItem = (item: ReferenceImageItem) => {
    if (mode === 'single') {
      setSelectedIds([item.publicId])
      return
    }
    setSelectedIds((prev) => {
      if (prev.includes(item.publicId)) return prev.filter((id) => id !== item.publicId)
      if (prev.length >= remaining) return prev
      return [...prev, item.publicId]
    })
  }

  const confirmPicker = () => {
    const byId = new Map(
      [...libraryItems, ...recentItems].map((item) => [item.publicId, item]),
    )
    const picked = selectedIds
      .map((id) => byId.get(id))
      .filter((item): item is ReferenceImageItem => Boolean(item))
      .map((item) =>
        referencePickerSubmitUrl(item.url, { bucket: item.bucket, objectKey: item.objectKey }),
      )
    mergeUrls(picked)
    setPickerOpen(false)
    setSelectedIds([])
    if (picked.length > 0) {
      showToast({
        message: mode === 'single' ? '已选择图片' : `已添加 ${picked.length} 张`,
        variant: 'success',
        autoCloseMs: 2200,
      })
    }
  }

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    e.target.value = ''
    if (!files?.length || disabled) return
    const limit = mode === 'single' ? 1 : remaining
    if (limit <= 0) {
      notifyAtLimit()
      return
    }
    setUploading(true)
    try {
      const added: string[] = []
      const failed: string[] = []
      for (const file of Array.from(files).slice(0, limit)) {
        const label = file.name.trim() || '未命名图片'
        if (file.size > REFERENCE_IMAGE_MAX_BYTES) {
          failed.push(`${label}（超过 ${REFERENCE_IMAGE_MAX_SIZE_LABEL}）`)
          continue
        }
        try {
          const res = await uploadReferenceImageToDisk(file, referenceImageUploadFolderPath(file))
          added.push(absolutePublicUploadUrl(res.url))
        } catch (err) {
          failed.push(label)
          pickerLogger.warn('参考图上传失败', label, err)
        }
      }
      // 部分成功也要落库已上传的图，且只出一条结论
      mergeUrls(added)
      if (failed.length > 0) {
        const brief = `${failed.slice(0, 2).join('、')}${failed.length > 2 ? '…' : ''}`
        showToast({
          message:
            added.length > 0
              ? `已添加 ${added.length} 张，${failed.length} 张失败：${brief}`
              : `参考图上传失败：${brief}`,
          variant: added.length > 0 ? 'warning' : 'error',
          autoCloseMs: added.length > 0 ? 6000 : 0,
        })
      } else if (added.length > 0) {
        showToast({
          message: mode === 'single' ? '造型图已更新' : `已添加 ${added.length} 张参考图`,
          variant: 'success',
          autoCloseMs: 2600,
        })
      }
      if (pickerOpen) {
        void loadTabPage(activeTab, true).catch((err: unknown) => {
          pickerLogger.warn('上传后刷新目录失败', err)
        })
      }
    } catch (err) {
      showApiErrorToast(showToast, err, '上传失败', 'error')
    } finally {
      setUploading(false)
    }
  }

  const clearSingle = () => {
    if (disabled) return
    onChange([])
  }

  const removeAt = (idx: number) => {
    if (disabled) return
    onChange(urls.filter((_, i) => i !== idx))
  }

  const hasImage = mode === 'single' ? urls.length > 0 : false
  const primaryUrl = urls[0]

  const pickerModal = (
    <LibraryReferencePickerModal
      angular={angular}
      open={pickerOpen}
      loading={catalogLoading}
      items={libraryItems}
      activeTab={activeTab}
      recentItems={recentItems}
      recentLoading={recentLoading}
      hasMore={
        (activeTab === 'recent' ? recentItems.length : libraryItems.length) <
        (activeTab === 'recent' ? recentTotal : catalogTotal)
      }
      selectedIds={selectedIds}
      remaining={mode === 'single' ? 1 : remaining}
      referenceUrls={mode === 'single' ? [] : urls}
      selectionMode={mode}
      onClearSelection={() => setSelectedIds([])}
      title={modalTitle}
      onClose={() => {
        setPickerOpen(false)
        setSelectedIds([])
      }}
      onToggle={toggleItem}
      onUseLocalUpload={() => {
        // 关键：必须先触发 hidden input 的 click，再关 modal。
        // 浏览器会把 setState 当作用户手势链中断，后续的 .click() 会被拦截。
        fileRef.current?.click()
        setPickerOpen(false)
        setSelectedIds([])
      }}
      onConfirm={confirmPicker}
      onTabChange={handleTabChange}
      onLoadMore={handleLoadMore}
      onThumbBroken={(pid) =>
        setSelectedIds((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : prev))
      }
      onDeletedFromLibrary={(pid) => {
        setSelectedIds((prev) => prev.filter((x) => x !== pid))
        void loadTabPage('gallery', true)
      }}
      runBlockingDelete={(action) => action()}
    />
  )

  const hiddenFileInput = (
    <input
      ref={fileRef}
      type="file"
      accept="image/*"
      multiple={mode !== 'single'}
      className="hidden"
      onChange={(e) => void onFileChange(e)}
    />
  )

  if (mode === 'single') {
    return (
      <div className={disabled ? 'pointer-events-none opacity-55' : ''}>
        <div className="space-y-2">
          <ChromeMediaUploadSlot
            angular={angular}
            density={compact ? 'compact' : 'default'}
            hasMedia={hasImage}
            uploading={uploading}
            disabled={disabled}
            emptyLabel={placeholderText}
            selectLabel={hasImage ? '更换图片' : '从图库选择'}
            onSelect={openPicker}
            onDelete={hasImage ? clearSingle : undefined}
            emptyIcon={<IconLibraryAllFolders className="h-5 w-5" aria-hidden />}
            preview={
              primaryUrl ? (
                <ThemedImage
                  src={primaryUrl}
                  alt=""
                  compact
                  interactiveSave
                  downloadNameHint="reference"
                  className={mediaFrameImageClass('contain')}
                />
              ) : undefined
            }
          />
          {hiddenFileInput}
          {hint ? <p className="text-xs leading-relaxed text-ink-soft">{hint}</p> : null}
        </div>
        {pickerModal}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div
        className={[
          chromeFieldBaseClass(angular),
          'overflow-hidden p-3',
          disabled ? 'pointer-events-none opacity-55' : '',
        ].join(' ')}
      >
        <div className="flex min-w-0 items-stretch gap-3 sm:gap-4">
          <div className={['relative shrink-0 h-[5.5rem] w-[5.5rem] sm:h-32 sm:w-32'].join(' ')}>
            <div
              className={[
                'scroll-region flex h-full w-full min-h-0 flex-col gap-1.5 overflow-y-auto overscroll-y-contain p-1',
                'border border-line bg-surface-muted/25',
                previewRound(angular),
              ].join(' ')}
            >
              {urls.length === 0 ? (
                <button
                  type="button"
                  onClick={openPicker}
                  className="flex flex-1 flex-col items-center justify-center gap-1 px-1 text-center"
                >
                  <IconLibraryAllFolders className="h-4 w-4 text-ink-soft" />
                  <span className="text-[10px] font-medium text-ink-soft">{placeholderText}</span>
                </button>
              ) : (
                urls.map((u, idx) => (
                  <div key={`${u}-${idx}`} className="relative shrink-0">
                    <ThemedImage
                      src={u}
                      alt=""
                      compact
                      className={[
                        'h-11 w-full border border-line object-cover',
                        previewRound(angular),
                      ].join(' ')}
                    />
                    <button
                      type="button"
                      className={[
                        'absolute right-0.5 top-0.5 rounded px-1 py-px text-[9px] font-bold text-white',
                        'bg-red-600/90 hover:bg-red-700',
                        angular ? 'rounded-none' : 'rounded-sm',
                      ].join(' ')}
                      aria-label={`删除参考图 ${idx + 1}`}
                      onClick={() => removeAt(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
            <ChromeToolbarButton
              type="button"
              className={btnClass}
              disabled={remaining <= 0 || uploading}
              onClick={openPicker}
            >
              {uploading ? '处理中…' : '从图库选择'}
            </ChromeToolbarButton>
          </div>
        </div>
        {hiddenFileInput}
      </div>

      {hint ? <p className="text-xs leading-relaxed text-ink-soft">{hint}</p> : null}
      {pickerModal}
    </div>
  )
}
